import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Badge,
  useColorModeValue,
  IconButton,
  Divider
} from '@chakra-ui/react';
import { FiSearch, FiX, FiTrendingUp, FiClock } from 'react-icons/fi';
import { SearchDiscoveryService, type SearchSuggestion } from '../services/searchDiscoveryService';
import IconWrapper from './IconWrapper';

interface SearchSuggestionsProps {
  onSearch: (query: string) => void;
  onSuggestionClick: (suggestion: SearchSuggestion) => void;
  placeholder?: string;
  initialQuery?: string;
}

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  onSearch,
  onSuggestionClick,
  placeholder = "Search for movies, series, comics...",
  initialQuery = ''
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const suggestionBg = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputColor = useColorModeValue('gray.900', 'white');
  const inputFocusBg = useColorModeValue('gray.100', 'gray.600');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    // Load search history from localStorage
    const history = localStorage.getItem('searchHistory');
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, []);

  // Sync with initialQuery prop when it changes
  useEffect(() => {
    if (initialQuery && initialQuery !== query) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.trim().length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const suggestionsData = await SearchDiscoveryService.getSearchSuggestions(query, 8);
        setSuggestions(suggestionsData);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setShowSuggestions(true);
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    // Add to search history
    const newHistory = [query, ...searchHistory.filter(h => h !== query)].slice(0, 10);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
    
    onSearch(query);
    setShowSuggestions(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.text);
    onSuggestionClick(suggestion);
    setShowSuggestions(false);
  };

  const handleHistoryClick = (historyItem: string) => {
    setQuery(historyItem);
    onSearch(historyItem);
    setShowSuggestions(false);
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'query':
        return FiSearch;
      case 'content':
        return FiTrendingUp;
      case 'creator':
        return FiClock;
      default:
        return FiSearch;
    }
  };

  const getSuggestionColor = (type: string) => {
    switch (type) {
      case 'query':
        return 'blue';
      case 'content':
        return 'green';
      case 'creator':
        return 'purple';
      default:
        return 'gray';
    }
  };

  return (
    <Box position="relative" w="100%">
      <HStack>
        <Input
          ref={inputRef}
          placeholder={placeholder}
          bg={inputBg}
          border="none"
          color={inputColor}
          _placeholder={{ color: placeholderColor }}
          _focus={{ bg: inputFocusBg }}
          value={query}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={() => setShowSuggestions(true)}
        />
        <Button
          colorScheme="purple"
          onClick={handleSearch}
          isDisabled={!query.trim()}
          leftIcon={<IconWrapper icon={FiSearch} size={16} />}
        >
          Search
        </Button>
      </HStack>

      {showSuggestions && (
        <Box
          ref={suggestionsRef}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="lg"
          zIndex={1000}
          mt={1}
          maxH="400px"
          overflowY="auto"
        >
          <VStack align="stretch" p={2}>
            {/* Search History */}
            {searchHistory.length > 0 && query.length === 0 && (
              <>
                <HStack justify="space-between" px={2} py={1}>
                  <Text fontSize="sm" fontWeight="bold" color={labelColor}>
                    Recent Searches
                  </Text>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    icon={<IconWrapper icon={FiX} size={12} />}
                    onClick={clearHistory}
                    aria-label="Clear history"
                  />
                </HStack>
                {searchHistory.slice(0, 5).map((historyItem, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    size="sm"
                    justifyContent="flex-start"
                    leftIcon={<IconWrapper icon={FiClock} size={14} />}
                    onClick={() => handleHistoryClick(historyItem)}
                    bg={suggestionBg}
                    _hover={{ bg: 'gray.100' }}
                  >
                    {historyItem}
                  </Button>
                ))}
                <Divider />
              </>
            )}

            {/* Search Suggestions */}
            {suggestions.length > 0 && (
              <>
                <Text fontSize="sm" fontWeight="bold" color={labelColor} px={2} py={1}>
                  Suggestions
                </Text>
                {suggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    size="sm"
                    justifyContent="flex-start"
                    leftIcon={<IconWrapper icon={getSuggestionIcon(suggestion.type)} size={14} />}
                    onClick={() => handleSuggestionClick(suggestion)}
                    bg={suggestionBg}
                    _hover={{ bg: 'gray.100' }}
                  >
                    <HStack justify="space-between" w="100%">
                      <Text>{suggestion.text}</Text>
                      <HStack gap={1}>
                        {suggestion.category && (
                          <Badge size="xs" colorScheme="gray">
                            {suggestion.category}
                          </Badge>
                        )}
                        <Badge size="xs" colorScheme={getSuggestionColor(suggestion.type)}>
                          {suggestion.type}
                        </Badge>
                        {suggestion.count > 0 && (
                          <Text fontSize="xs" color="gray.500">
                            {suggestion.count}
                          </Text>
                        )}
                      </HStack>
                    </HStack>
                  </Button>
                ))}
              </>
            )}

            {/* No suggestions */}
            {query.length > 0 && suggestions.length === 0 && !isLoading && (
              <Text fontSize="sm" color={textColor} px={2} py={2}>
                No suggestions found for "{query}"
              </Text>
            )}

            {/* Loading state */}
            {isLoading && (
              <Text fontSize="sm" color={textColor} px={2} py={2}>
                Loading suggestions...
              </Text>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default SearchSuggestions;
